/**
 * BenchmarkPage — Playground Benchmark test case manager.
 *
 * Two views: Dashboard (case list) and Form (add/edit). Cases persist to the
 * authenticated user's memory bucket via /api/users/benchmark/cases. File uploads
 * go directly to GCS via api.uploadBlob — the public_url is stored on the case.
 *
 * No Bootstrap. All styling uses inline V theme variables so it renders identically
 * to the rest of Playground without polluting the main bundle.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../services/api';

// Mirror of PlaygroundView's V theme. Kept local to avoid forcing an export from
// PlaygroundView.jsx (which is already 7000+ lines).
const V = {
  bg: '#f5f5f7',
  bgCard: '#ffffff',
  bgHover: '#eeeef0',
  bgSubtle: '#fafbfc',
  border: '#e5e5ea',
  borderStrong: '#d8d8dc',
  text: '#1d1d1f',
  textDim: '#6e6e73',
  textFaint: '#a1a1a6',
  accent: '#5856d6',
  accentHover: '#4a48c4',
  accentDim: '#eeedf5',
  accentSoft: '#f4f3fa',
  green: '#34c759',
  red: '#ff3b30',
  yellow: '#ff9500',
  blue: '#007aff',
  fontUi: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
};

// /api/upload/presign currently only accepts these extensions. Use the same
// allowlist on the file picker so users can't pick something we'd reject.
const ACCEPT_VISUAL = 'image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime';
// §5 Output Reference also accepts self-contained .html documents (see upload.py whitelist).
const ACCEPT_REFERENCE = ACCEPT_VISUAL + ',text/html,.html,application/pdf,.pdf,application/msword,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx';

// Quick-pick tags shown at the top of each case form. Free-text tags are also
// accepted — these are just starter suggestions so labels stay consistent.
const DEFAULT_TAGS = ['intention', 'quick response', 'general'];

/* eslint-disable react-refresh/only-export-components */
export const isVideoUrl = (url) => /\.(mp4|webm|mov)(\?|$)/i.test(url);
export const isHtmlUrl = (url) => /\.html(\?|$)/i.test(url);
const hasMediaExt = (url) =>
  /\.(jpe?g|png|webp|gif|avif|heic|mp4|webm|mov|html)(\?|$)/i.test(url);
export const isLinkUrl = (url) => /^https?:\/\//i.test(url) && !hasMediaExt(url);
const isProductUrl = (url) => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    return /\.(com|net|org)$/.test(host) && /^(shop\.|store\.|product\.)/.test(host)
      || /amazon\.|ebay\.|etsy\.|walmart\.|target\.|aliexpress\.|shopify\.|fashionnova\.|coquetryclothing\.|3wishes\.|shein\.|temu\.|wish\./.test(host)
      || /\/products?\//i.test(url) || /\/shop\//i.test(url) || /\/cart/i.test(url);
  } catch { return false; }
};
/* eslint-enable react-refresh/only-export-components */

function getExt(file) {
  const m = (file?.name || '').match(/\.([a-z0-9]+)$/i);
  return (m ? m[1] : 'jpg').toLowerCase();
}

// ─────────────────────────────────────────────────────────────────────────────
// Style helpers
// ─────────────────────────────────────────────────────────────────────────────

const buttonStyle = (bg) => ({
  background: bg,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  transition: 'all 0.15s',
});

const inputStyle = () => ({
  width: '100%',
  border: `1px solid ${V.border}`,
  borderRadius: 8,
  padding: '10px 14px',
  fontSize: 13,
  fontFamily: 'inherit',
  background: '#fff',
  color: V.text,
  outline: 'none',
  boxSizing: 'border-box',
  resize: 'vertical',
});

const tagStyle = (color) => ({
  background: `${color}1f`,
  color,
  padding: '3px 8px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
});

// ─────────────────────────────────────────────────────────────────────────────
// DropZone (reused by Visual Material + Output Reference)
// ─────────────────────────────────────────────────────────────────────────────

function DropZone({ accept, onFiles, fileTypeLabel = 'JPG · PNG · WebP · MP4 · WebM · MOV' }) {
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [mouseOver, setMouseOver] = useState(false);

  const active = hover || mouseOver;

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
      onDragOver={(e) => { e.preventDefault(); setHover(true); }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setHover(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        const files = Array.from(e.dataTransfer?.files || []);
        if (files.length) onFiles(files);
      }}
      style={{
        border: `2px dashed ${active ? V.accent : V.borderStrong}`,
        borderRadius: 14,
        padding: '32px 16px',
        textAlign: 'center',
        cursor: 'pointer',
        background: hover
          ? V.accentDim
          : mouseOver
          ? V.accentSoft
          : V.bgSubtle,
        transition: 'all 0.18s',
        userSelect: 'none',
      }}
    >
      <div style={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        background: active ? V.accent : '#fff',
        color: active ? '#fff' : V.textDim,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 22,
        marginBottom: 10,
        border: `1px solid ${active ? V.accent : V.border}`,
        boxShadow: active ? '0 4px 12px rgba(88,86,214,0.25)' : '0 1px 2px rgba(0,0,0,0.04)',
        transition: 'all 0.18s',
      }}>
        ⬆
      </div>
      <div style={{
        fontSize: 13.5,
        color: active ? V.accent : V.text,
        fontWeight: 600,
        marginBottom: 4,
      }}>
        {hover ? 'Drop files to upload' : 'Click to browse or drag & drop'}
      </div>
      <div style={{ fontSize: 11.5, color: V.textFaint }}>
        {fileTypeLabel}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ThumbGrid — renders uploaded GCS URLs with hover-x removal
// ─────────────────────────────────────────────────────────────────────────────

export function ThumbGrid({ urls, onRemove }) {
  if (!urls.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
      {urls.map((url, idx) => {
        const html = isHtmlUrl(url);
        const link = !html && isLinkUrl(url);
        const video = !html && !link && isVideoUrl(url);
        let linkHost = '';
        if (link) {
          try { linkHost = new URL(url).hostname.replace(/^www\./, ''); }
          catch (_) { linkHost = url; }
        }
        return (
          <div
            key={`${idx}-${url}`}
            style={{
              position: 'relative',
              width: 90,
              height: 90,
              borderRadius: 8,
              overflow: 'hidden',
              border: `1px solid ${V.border}`,
              background: '#f3f4f6',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              const o = e.currentTarget.querySelector('[data-overlay]');
              if (o) o.style.opacity = '1';
              const btn = e.currentTarget.querySelector('button[aria-label="Remove file"]');
              if (btn) btn.style.pointerEvents = 'auto';
            }}
            onMouseLeave={(e) => {
              const o = e.currentTarget.querySelector('[data-overlay]');
              if (o) o.style.opacity = '0';
              const btn = e.currentTarget.querySelector('button[aria-label="Remove file"]');
              if (btn) btn.style.pointerEvents = 'none';
            }}
          >
            {html ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={url.split('/').pop().split('?')[0] || 'document.html'}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  padding: 6,
                  textDecoration: 'none',
                  color: V.text,
                  background: '#fff',
                  boxSizing: 'border-box',
                }}
              >
                <span style={{ fontSize: 26, lineHeight: 1 }} aria-hidden>📄</span>
                <span
                  style={{
                    fontSize: 10,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                >
                  {url.split('/').pop().split('?')[0] || 'document.html'}
                </span>
              </a>
            ) : link ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={url}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  padding: 6,
                  textDecoration: 'none',
                  color: V.text,
                  background: '#fff',
                  boxSizing: 'border-box',
                }}
              >
                <span style={{ fontSize: 26, lineHeight: 1 }} aria-hidden>🔗</span>
                <span
                  style={{
                    fontSize: 10,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                >
                  {linkHost}
                </span>
              </a>
            ) : video ? (
              <video
                src={url}
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <img
                src={url}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            )}
            <div
              data-overlay
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRemove(idx); }}
                style={{
                  background: V.red,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
                aria-label="Remove file"
              >
                ✕
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section — labeled form section wrapper
// ─────────────────────────────────────────────────────────────────────────────

function Section({ index, title, hint, titleExtra, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: V.accentSoft,
            color: V.accent,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            border: `1px solid ${V.accentDim}`,
            flexShrink: 0,
          }}
        >
          {index}
        </span>
        <label style={{ fontWeight: 600, fontSize: 15, color: V.text, letterSpacing: '-0.01em' }}>
          {title}
        </label>
        {titleExtra}
      </div>
      <div style={{ fontSize: 12.5, color: V.textDim, marginBottom: 12, marginLeft: 38, lineHeight: 1.5 }}>
        {hint}
      </div>
      <div style={{ marginLeft: 0 }}>{children}</div>
    </div>
  );
}

function FileCountNote({ count, color }) {
  return (
    <div style={{
      marginTop: 8,
      padding: '6px 10px',
      borderRadius: 6,
      background: '#f3f4f6',
      fontSize: 11,
      color: V.textDim,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      <span>ⓘ Files uploaded directly to GCS</span>
      <span style={{
        background: color,
        color: '#fff',
        padding: '2px 10px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
      }}>
        {count} file{count !== 1 ? 's' : ''}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard view
// ─────────────────────────────────────────────────────────────────────────────

function DashboardView({
  cases,
  loading,
  error,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  selectedTags,
  onToggleTagFilter,
}) {
  const allTags = Array.from(
    new Set(cases.flatMap((c) => (Array.isArray(c?.tags) ? c.tags : [])))
  ).sort();
  const visibleCases =
    selectedTags.length === 0
      ? cases.map((c, origIdx) => ({ c, origIdx }))
      : cases
          .map((c, origIdx) => ({ c, origIdx }))
          .filter(({ c }) => {
            const ct = Array.isArray(c?.tags) ? c.tags : [];
            return selectedTags.every((t) => ct.includes(t));
          });
  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 24,
        marginBottom: 28,
        borderBottom: `1px solid ${V.border}`,
        gap: 16,
      }}>
        <div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 10px',
            background: V.accentSoft,
            color: V.accent,
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            marginBottom: 10,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}>
            <span>🧪</span>
            <span>Benchmark</span>
          </div>
          <h2 style={{
            fontSize: 28,
            fontWeight: 700,
            margin: 0,
            color: V.text,
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
          }}>
            Benchmark Dashboard
          </h2>
          <div style={{ fontSize: 13, color: V.textDim, marginTop: 6 }}>
            Manage your test cases for evaluating agent quality.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0, marginTop: 4 }}>
          <button
            type="button"
            onClick={onExport}
            disabled={cases.length === 0}
            style={{
              ...buttonStyle(cases.length > 0 ? V.green : V.bgHover),
              color: cases.length > 0 ? '#fff' : V.textFaint,
              cursor: cases.length > 0 ? 'pointer' : 'not-allowed',
              boxShadow: cases.length > 0 ? '0 1px 3px rgba(52,199,89,0.3)' : 'none',
            }}
          >
            ⬇ Export JSON
            <span style={{
              marginLeft: 6,
              padding: '2px 8px',
              borderRadius: 10,
              background: cases.length > 0 ? 'rgba(255,255,255,0.25)' : V.border,
              color: cases.length > 0 ? '#fff' : V.textFaint,
              fontSize: 11,
              fontWeight: 700,
            }}>
              {cases.length}
            </span>
          </button>
          <button
            type="button"
            onClick={onAdd}
            style={{
              ...buttonStyle(V.accent),
              boxShadow: '0 1px 3px rgba(88,86,214,0.35), 0 4px 12px rgba(88,86,214,0.18)',
            }}
          >
            + Add New Case
          </button>
        </div>
      </div>

      {allTags.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            marginBottom: 20,
            padding: '12px 14px',
            background: V.bgSubtle,
            border: `1px solid ${V.border}`,
            borderRadius: 12,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: V.textDim,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              marginRight: 4,
            }}
          >
            🏷 Filter
          </span>
          {allTags.map((t) => {
            const active = selectedTags.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => onToggleTagFilter(t)}
                style={{
                  background: active ? V.accent : '#fff',
                  color: active ? '#fff' : V.accent,
                  border: `1px solid ${active ? V.accent : V.border}`,
                  borderRadius: 999,
                  padding: '5px 12px',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  boxShadow: active ? '0 1px 2px rgba(88,86,214,0.25)' : 'none',
                }}
              >
                {active ? '✓ ' : ''}{t}
              </button>
            );
          })}
          {selectedTags.length > 0 && (
            <button
              type="button"
              onClick={() => selectedTags.forEach((t) => onToggleTagFilter(t))}
              style={{
                background: 'transparent',
                color: V.textDim,
                border: 'none',
                padding: '5px 8px',
                fontSize: 11.5,
                fontWeight: 500,
                cursor: 'pointer',
                marginLeft: 'auto',
                textDecoration: 'underline',
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', color: V.textDim, padding: 40, fontSize: 13 }}>
          Loading cases…
        </div>
      )}
      {error && !loading && (
        <div style={{
          color: V.red,
          padding: 16,
          border: `1px solid ${V.red}`,
          borderRadius: 8,
          background: '#fef2f2',
          fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {!loading && !error && cases.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '72px 32px',
          border: `1.5px dashed ${V.borderStrong}`,
          borderRadius: 16,
          background: `radial-gradient(circle at center top, ${V.accentSoft} 0%, ${V.bgSubtle} 70%)`,
        }}>
          <div style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 32,
            marginBottom: 18,
            border: `1px solid ${V.border}`,
            boxShadow: '0 4px 16px rgba(88,86,214,0.10)',
          }}>
            🧪
          </div>
          <div style={{
            fontSize: 18,
            color: V.text,
            marginBottom: 8,
            fontWeight: 700,
            letterSpacing: '-0.01em',
          }}>
            No benchmark cases yet
          </div>
          <div style={{ fontSize: 13.5, color: V.textDim, marginBottom: 24, lineHeight: 1.5 }}>
            Create your first test case to start benchmarking your agents.
          </div>
          <button
            type="button"
            onClick={onAdd}
            style={{
              ...buttonStyle(V.accent),
              padding: '10px 22px',
              fontSize: 13.5,
              boxShadow: '0 1px 3px rgba(88,86,214,0.35), 0 6px 16px rgba(88,86,214,0.20)',
            }}
          >
            + Create First Case
          </button>
        </div>
      )}

      {!loading && !error && cases.length > 0 && visibleCases.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '48px 32px',
            border: `1.5px dashed ${V.borderStrong}`,
            borderRadius: 16,
            background: V.bgSubtle,
            color: V.textDim,
            fontSize: 13.5,
          }}
        >
          No cases match the selected tag filter.
        </div>
      )}

      {!loading && !error && visibleCases.map(({ c, origIdx }) => {
        const promptText = c?.input?.messages?.[0]?.content || '';
        const promptExcerpt =
          promptText.length > 80 ? promptText.slice(0, 80) + '…' : promptText;
        const visualCount = c?.input?.messages?.[0]?.image_urls?.length || 0;
        const refCount = c?.expected_output?.references?.files?.length || 0;
        const metricCount = c?.expected_output?.checkpoints?.length || 0;
        const caseTags = Array.isArray(c?.tags) ? c.tags : [];

        return (
          <div
            key={c?.case_id || origIdx}
            style={{
              position: 'relative',
              border: `1px solid ${V.border}`,
              borderRadius: 14,
              padding: '18px 20px 18px 22px',
              marginBottom: 12,
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              transition: 'border-color 0.18s, box-shadow 0.18s, transform 0.18s, background 0.18s',
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = V.accent;
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(88,86,214,0.08), 0 8px 24px rgba(88,86,214,0.10)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.background = `linear-gradient(135deg, #fff 0%, ${V.accentSoft} 100%)`;
              const bar = e.currentTarget.querySelector('[data-accent-bar]');
              if (bar) bar.style.transform = 'scaleY(1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = V.border;
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.background = '#fff';
              const bar = e.currentTarget.querySelector('[data-accent-bar]');
              if (bar) bar.style.transform = 'scaleY(0)';
            }}
          >
            <div
              data-accent-bar
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                background: V.accent,
                transform: 'scaleY(0)',
                transformOrigin: 'center',
                transition: 'transform 0.2s',
              }}
            />
            <div
              style={{ flex: 1, paddingRight: 16, minWidth: 0 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 10 }}>
                <span style={{
                  background: V.accent,
                  color: '#fff',
                  padding: '3px 9px',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  boxShadow: '0 1px 2px rgba(88,86,214,0.3)',
                }}>
                  #{origIdx + 1}
                </span>
                <h5 style={{
                  fontSize: 14.5,
                  fontWeight: 600,
                  margin: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                  color: V.text,
                  letterSpacing: '-0.01em',
                }}>
                  {c?.input?.intention || 'Untitled Intention'}
                </h5>
              </div>
              <div style={{
                fontSize: 12.5,
                color: V.textDim,
                marginBottom: 12,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.5,
              }}>
                💬 {promptExcerpt || '(no prompt)'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={tagStyle(V.green)}>✓ {metricCount} Metrics</span>
                {visualCount > 0 && <span style={tagStyle(V.accent)}>🖼 {visualCount} Visuals</span>}
                {refCount > 0 && <span style={tagStyle(V.textDim)}>📄 {refCount} Refs</span>}
                {caseTags.map((t) => (
                  <span
                    key={t}
                    style={{
                      background: '#fff',
                      color: V.accent,
                      border: `1px solid ${V.accentDim}`,
                      padding: '2px 9px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                  >
                    🏷 {t}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => onEdit(origIdx)}
                style={{
                  background: 'transparent',
                  color: V.accent,
                  border: `1px solid ${V.border}`,
                  borderRadius: 8,
                  padding: '7px 13px',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = V.accent;
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.borderColor = V.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = V.accent;
                  e.currentTarget.style.borderColor = V.border;
                }}
              >
                ✏ Edit
              </button>
              <button
                type="button"
                onClick={() => onDelete(origIdx)}
                style={{
                  background: 'transparent',
                  color: V.red,
                  border: `1px solid ${V.border}`,
                  borderRadius: 8,
                  padding: '7px 13px',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = V.red;
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.borderColor = V.red;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = V.red;
                  e.currentTarget.style.borderColor = V.border;
                }}
              >
                🗑 Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Form view
// ─────────────────────────────────────────────────────────────────────────────

function FormView({
  editIndex,
  intention, setIntention,
  prompt, setPrompt,
  visualUrls, onVisualFiles, onRemoveVisual,
  refUrls, onRefFiles, onRemoveRef,
  refUrlInput, onRefUrlInputChange, onAddRefUrl, refUrlError,
  refText, onRefTextChange,
  metrics, onUpdateMetric, onAddMetric, onRemoveMetric, weightPercent,
  tags, tagInput, onTagInputChange, onAddTag, onRemoveTag, suggestedTags,
  uploading, submitting, onSubmit, onCancel,
  scaffoldUrl, onScaffoldUrlChange, scaffoldLoading, scaffoldError, onRunScaffold,
  referenceNote, autoFilledFields, onTouchAutoField,
  scaffoldTurns, selectedTurnIndex, onHighlightTurn, onConfirmTurn, onRemoveTurn,
  saveGateBlocked,
}) {
  const autoChip = (field) => {
    if (!autoFilledFields || !autoFilledFields.has(field)) return null;
    return (
      <span
        title="Auto-filled from a shared session. Click the field to confirm you've reviewed it."
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          marginLeft: 8,
          padding: '2px 8px',
          borderRadius: 999,
          background: '#fff7e0',
          color: '#8a6d00',
          border: '1px solid #f2d98a',
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}
      >
        ⚡ auto
      </span>
    );
  };
  const handleTouch = (field) => () => {
    if (onTouchAutoField && autoFilledFields && autoFilledFields.has(field)) {
      onTouchAutoField(field);
    }
  };
  return (
    <form onSubmit={onSubmit}>
      <button
        type="button"
        onClick={onCancel}
        style={{
          background: 'transparent',
          color: V.textDim,
          border: 'none',
          padding: '6px 0',
          marginBottom: 14,
          fontSize: 13,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          transition: 'color 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = V.accent; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = V.textDim; }}
        title="Back to dashboard"
      >
        ← Back to Dashboard
      </button>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 24,
        marginBottom: 28,
        borderBottom: `1px solid ${V.border}`,
        gap: 16,
      }}>
        <div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 10px',
            background: V.accentSoft,
            color: V.accent,
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            marginBottom: 10,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}>
            <span>{editIndex >= 0 ? '✏' : '+'}</span>
            <span>{editIndex >= 0 ? `Editing Case #${editIndex + 1}` : 'New Case'}</span>
          </div>
          <h2 style={{
            fontSize: 28,
            fontWeight: 700,
            margin: 0,
            color: V.text,
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
          }}>
            {editIndex >= 0 ? 'Edit Benchmark Case' : 'Add Benchmark Case'}
          </h2>
          <div style={{ fontSize: 13, color: V.textDim, marginTop: 6 }}>
            Define the input, expected output, and evaluation metrics.
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          style={{
            background: 'transparent',
            color: V.textDim,
            border: `1px solid ${V.border}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 12.5,
            cursor: 'pointer',
            flexShrink: 0,
            marginTop: 4,
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = V.bgHover;
            e.currentTarget.style.color = V.text;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = V.textDim;
          }}
        >
          ✕ Cancel
        </button>
      </div>

      {editIndex < 0 && (
        <div
          style={{
            marginBottom: 28,
            padding: 16,
            background: '#fff7e0',
            border: '1px solid #f2d98a',
            borderRadius: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 16 }}>⚡</span>
            <label style={{ fontWeight: 600, fontSize: 14, color: '#6b5200', letterSpacing: '-0.01em' }}>
              Import from playground share URL
            </label>
          </div>
          <div style={{ fontSize: 12, color: '#8a6d00', marginBottom: 10, lineHeight: 1.5 }}>
            Paste a share link from the playground chat page to auto-fill Visual Material, Input Prompt, Input Intention, and Output Guideline checkpoints. You'll still review every field before saving.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <input
              type="url"
              value={scaffoldUrl}
              onChange={(e) => onScaffoldUrlChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onRunScaffold();
                }
              }}
              placeholder={`${window.location.origin}/playground/share/…`}
              disabled={scaffoldLoading}
              style={{ ...inputStyle(), flex: 1, margin: 0 }}
              aria-label="Playground share URL"
            />
            <button
              type="button"
              onClick={onRunScaffold}
              disabled={scaffoldLoading || !scaffoldUrl.trim()}
              style={{
                ...buttonStyle(V.accent),
                opacity: (scaffoldLoading || !scaffoldUrl.trim()) ? 0.55 : 1,
                cursor: (scaffoldLoading || !scaffoldUrl.trim()) ? 'not-allowed' : 'pointer',
                minWidth: 96,
                justifyContent: 'center',
              }}
            >
              {scaffoldLoading ? '⏳ Running…' : '▶ Run'}
            </button>
          </div>
          {scaffoldError ? (
            <div style={{ color: V.red, fontSize: 12, marginTop: 8 }}>
              {scaffoldError}
            </div>
          ) : null}
          {referenceNote ? (
            <div
              style={{
                marginTop: 10,
                padding: '8px 12px',
                background: '#fff',
                border: '1px solid #f2d98a',
                borderRadius: 8,
                fontSize: 12,
                color: '#6b5200',
                lineHeight: 1.5,
              }}
              title="Why the LLM judged this reference as a good answer."
            >
              <strong style={{ fontWeight: 600 }}>Reference note:</strong> {referenceNote}
            </div>
          ) : null}
        </div>
      )}

      {scaffoldTurns.length > 0 && (
        <div
          style={{
            marginBottom: 28,
            padding: 16,
            background: '#f0f7ff',
            border: '1px solid #a8d1f7',
            borderRadius: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 16 }}>🔄</span>
            <label style={{ fontWeight: 600, fontSize: 14, color: '#1a4d80' }}>
              Multi-turn session — select the best turn
            </label>
          </div>
          <div style={{ fontSize: 12, color: '#3a6d99', marginBottom: 12, lineHeight: 1.5 }}>
            This session has {scaffoldTurns.length} turns. Click a turn to select it, then confirm to run per-turn analysis.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {scaffoldTurns.map((turn) => {
              const isSelected = selectedTurnIndex === turn.index;
              const hasSel = selectedTurnIndex != null;
              const dimmed = hasSel && !isSelected;
              return (
              <div
                key={turn.index}
                style={{
                  position: 'relative',
                  padding: '12px 40px 12px 12px',
                  background: isSelected ? '#fff5f5' : dimmed ? '#f5f5f5' : '#fff',
                  border: isSelected ? '2px solid #e53e3e' : '1px solid #d0e3f5',
                  borderRadius: 8,
                  cursor: 'pointer',
                  opacity: dimmed ? 0.5 : 1,
                  transition: 'border-color 0.15s, background 0.15s, opacity 0.15s',
                }}
                onClick={() => onHighlightTurn(turn)}
                onMouseEnter={(e) => { if (!isSelected && !dimmed) { e.currentTarget.style.borderColor = '#4a9ade'; e.currentTarget.style.background = '#f0f8ff'; } }}
                onMouseLeave={(e) => { if (!isSelected && !dimmed) { e.currentTarget.style.borderColor = '#d0e3f5'; e.currentTarget.style.background = '#fff'; } }}
              >
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRemoveTurn(turn.index); }}
                  style={{
                    position: 'absolute', top: 6, right: 6,
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 16, color: '#999', lineHeight: 1,
                  }}
                  title="Remove this turn"
                >
                  ✕
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 22, height: 22, borderRadius: '50%',
                    background: '#e0eef9', fontSize: 11, fontWeight: 700, color: '#2a6aab',
                  }}>
                    {turn.index + 1}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1a4d80' }}>
                    Turn {turn.index + 1}
                  </span>
                  <span style={{ fontSize: 11, color: '#6a8fad' }}>
                    {turn.photos.length > 0 && `📷 ${turn.photos.length}`}
                    {turn.photos.length > 0 && turn.agent_images.length > 0 && ' · '}
                    {turn.agent_images.length > 0 && `🖼 ${turn.agent_images.length} results`}
                  </span>
                </div>
                <div style={{
                  fontSize: 12, color: '#444', lineHeight: 1.4,
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  💬 {turn.prompt || '(no prompt)'}
                </div>
                {turn.agent_images.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                    {turn.agent_images.slice(0, 4).map((url, j) => (
                      <img
                        key={j}
                        src={url}
                        alt=""
                        style={{
                          width: 48, height: 48, objectFit: 'cover',
                          borderRadius: 4, border: '1px solid #ddd',
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>
          {selectedTurnIndex != null ? (
            <button
              type="button"
              onClick={onConfirmTurn}
              disabled={scaffoldLoading}
              style={{
                marginTop: 12, padding: '8px 20px',
                background: scaffoldLoading ? '#999' : '#e53e3e', color: '#fff', border: 'none',
                borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: scaffoldLoading ? 'wait' : 'pointer',
                opacity: scaffoldLoading ? 0.7 : 1,
              }}
            >
              {scaffoldLoading ? '⏳ Analyzing…' : `✓ Use Turn ${selectedTurnIndex + 1} — Analyze`}
            </button>
          ) : (
            <div style={{ fontSize: 12, color: '#3a6d99', marginTop: 10 }}>
              Click a turn to select it, then confirm to analyze.
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              background: V.accentSoft,
              color: V.accent,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              border: `1px solid ${V.accentDim}`,
              flexShrink: 0,
            }}
          >
            🏷
          </span>
          <label style={{ fontWeight: 600, fontSize: 15, color: V.text, letterSpacing: '-0.01em' }}>
            Tags
          </label>
        </div>
        <div style={{ fontSize: 12.5, color: V.textDim, marginBottom: 12, marginLeft: 38, lineHeight: 1.5 }}>
          Label this case so you can filter on the dashboard. Pick a suggestion or type your own.
        </div>

        {/* Quick-pick suggestion row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          {suggestedTags.map((s) => {
            const picked = tags.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => onAddTag(s)}
                disabled={picked}
                style={{
                  background: picked ? V.accentSoft : '#fff',
                  color: picked ? V.textFaint : V.accent,
                  border: `1px solid ${picked ? V.accentDim : V.border}`,
                  borderRadius: 999,
                  padding: '5px 12px',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: picked ? 'default' : 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!picked) {
                    e.currentTarget.style.background = V.accent;
                    e.currentTarget.style.color = '#fff';
                    e.currentTarget.style.borderColor = V.accent;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!picked) {
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.color = V.accent;
                    e.currentTarget.style.borderColor = V.border;
                  }
                }}
              >
                {picked ? '✓ ' : '+ '}{s}
              </button>
            );
          })}
        </div>

        {/* Active tags (removable) */}
        {tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {tags.map((t, i) => (
              <span
                key={`${t}-${i}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: V.accent,
                  color: '#fff',
                  borderRadius: 999,
                  padding: '5px 4px 5px 12px',
                  fontSize: 12,
                  fontWeight: 500,
                  boxShadow: '0 1px 2px rgba(88,86,214,0.25)',
                }}
              >
                {t}
                <button
                  type="button"
                  onClick={() => onRemoveTag(i)}
                  aria-label={`Remove tag ${t}`}
                  style={{
                    background: 'rgba(255,255,255,0.22)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    fontSize: 11,
                    lineHeight: 1,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Free-text tag input */}
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={tagInput}
            onChange={(e) => onTagInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAddTag(tagInput);
              }
            }}
            placeholder="Type a custom tag and press Enter"
            style={{ ...inputStyle(), flex: 1 }}
          />
          <button
            type="button"
            onClick={() => onAddTag(tagInput)}
            disabled={!tagInput.trim()}
            style={{
              ...buttonStyle(tagInput.trim() ? V.accent : V.bgHover),
              color: tagInput.trim() ? '#fff' : V.textFaint,
              cursor: tagInput.trim() ? 'pointer' : 'not-allowed',
              flexShrink: 0,
            }}
          >
            + Add Tag
          </button>
        </div>
      </div>

      <div onFocusCapture={handleTouch('visual')} onClick={handleTouch('visual')}>
        <Section
          index={1}
          title="Visual Material"
          hint="Click or drag files to add images/videos. Each file is uploaded to GCS individually."
          titleExtra={autoChip('visual')}
        >
          <DropZone accept={ACCEPT_VISUAL} onFiles={onVisualFiles} />
          <ThumbGrid urls={visualUrls} onRemove={onRemoveVisual} />
          <FileCountNote count={visualUrls.length} color={V.accent} />
        </Section>
      </div>

      <div onFocusCapture={handleTouch('intention')} onClick={handleTouch('intention')}>
        <Section
          index={2}
          title="Input Intention"
          hint="What is the user trying to achieve? Describe the underlying goal."
          titleExtra={autoChip('intention')}
        >
          <textarea
            value={intention}
            onChange={(e) => setIntention(e.target.value)}
            rows={2}
            placeholder="e.g. User wants to change the living room style to modern minimalist…"
            style={inputStyle()}
          />
        </Section>
      </div>

      <div onFocusCapture={handleTouch('prompt')} onClick={handleTouch('prompt')}>
        <Section
          index={3}
          title="Input Prompt"
          hint="Simulated user prompt or system instruction that initializes this case."
          titleExtra={autoChip('prompt')}
        >
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            placeholder="Enter the exact prompt text here…"
            style={inputStyle()}
          />
        </Section>
      </div>

      <div onFocusCapture={handleTouch('metrics')} onClick={handleTouch('metrics')}>
      <Section
        index={4}
        title="Output Guideline"
        hint="Provide key metrics to define the standard of success. Weights are auto-distributed evenly across filled rows."
        titleExtra={autoChip('metrics')}
      >
        <div style={{
          background: V.bgSubtle,
          padding: 16,
          borderRadius: 12,
          border: `1px solid ${V.border}`,
        }}>
          {metrics.map((m, i) => {
            const isFilled = m.trim().length > 0;
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                  background: '#fff',
                  padding: 10,
                  borderRadius: 10,
                  border: `1px solid ${V.border}`,
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
              >
                <input
                  type="text"
                  value={m}
                  onChange={(e) => onUpdateMetric(i, e.target.value)}
                  placeholder="e.g. Response time < 5s"
                  required
                  style={{
                    ...inputStyle(),
                    flex: 1,
                    border: 'none',
                    padding: '8px 12px',
                  }}
                />
                <span style={{
                  background: isFilled ? V.accentSoft : V.bgHover,
                  color: isFilled ? V.accent : V.textFaint,
                  padding: '6px 12px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  minWidth: 48,
                  textAlign: 'center',
                  border: `1px solid ${isFilled ? V.accentDim : V.border}`,
                }}>
                  {isFilled ? `${weightPercent}%` : '—'}
                </span>
                <button
                  type="button"
                  onClick={() => onRemoveMetric(i)}
                  disabled={metrics.length === 1}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${metrics.length === 1 ? V.border : V.border}`,
                    color: metrics.length === 1 ? V.textFaint : V.red,
                    borderRadius: 8,
                    padding: '6px 10px',
                    cursor: metrics.length === 1 ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (metrics.length === 1) return;
                    e.currentTarget.style.background = V.red;
                    e.currentTarget.style.color = '#fff';
                    e.currentTarget.style.borderColor = V.red;
                  }}
                  onMouseLeave={(e) => {
                    if (metrics.length === 1) return;
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = V.red;
                    e.currentTarget.style.borderColor = V.border;
                  }}
                  title="Remove metric"
                >
                  🗑
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={onAddMetric}
            style={{
              background: '#fff',
              border: `1px dashed ${V.accent}`,
              color: V.accent,
              borderRadius: 10,
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: 12.5,
              fontWeight: 600,
              marginTop: 4,
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = V.accentSoft;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
            }}
          >
            + Add Metric
          </button>
        </div>
      </Section>
      </div>

      <div onFocusCapture={handleTouch('refs')} onClick={handleTouch('refs')}>
      <Section
        index={5}
        title="Output Reference"
        hint="Upload a file (image, video, HTML, PDF, or DOC) — or paste a link (e.g. a /playground/share/ URL) for live references."
        titleExtra={autoChip('refs')}
      >
        <DropZone
          accept={ACCEPT_REFERENCE}
          onFiles={onRefFiles}
          fileTypeLabel="JPG · PNG · WebP · MP4 · WebM · MOV · HTML · PDF · DOC"
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <input
              type="url"
              value={refUrlInput}
              onChange={(e) => onRefUrlInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onAddRefUrl();
                }
              }}
              placeholder={`或粘贴链接 — e.g. ${window.location.origin}/playground/share/…`}
              style={{ ...inputStyle(), margin: 0 }}
              aria-label="Output reference URL"
            />
            {refUrlError ? (
              <div style={{ color: V.red, fontSize: 11, marginTop: 4 }}>{refUrlError}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onAddRefUrl}
            disabled={!refUrlInput.trim()}
            style={{
              ...buttonStyle(V.accent),
              opacity: refUrlInput.trim() ? 1 : 0.55,
              cursor: refUrlInput.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            + Add URL
          </button>
        </div>
        <ThumbGrid urls={refUrls} onRemove={onRemoveRef} />
        <FileCountNote count={refUrls.length} color={V.textDim} />
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: V.textDim, marginBottom: 6 }}>
            Reference text (optional) — describe what the agent should deliver,
            or paste the reference answer text here.
          </div>
          <textarea
            value={refText}
            onChange={(e) => onRefTextChange(e.target.value)}
            rows={3}
            placeholder="e.g. The agent proposes a minimalist living-room redesign with ≥3 concrete furniture picks…"
            style={inputStyle()}
            aria-label="Output reference text"
          />
        </div>
      </Section>
      </div>

      <hr style={{ border: 'none', borderTop: `1px solid ${V.border}`, margin: '28px 0 24px' }} />
      {saveGateBlocked ? (
        <div
          style={{
            marginBottom: 10,
            padding: '10px 14px',
            borderRadius: 10,
            background: '#fff7e0',
            border: '1px solid #f2d98a',
            color: '#6b5200',
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          ⚡ Auto-filled fields need a review — click into each <strong>⚡ auto</strong> section once to confirm you've read it, then Save becomes available.
        </div>
      ) : null}
      <button
        type="submit"
        disabled={submitting || uploading || saveGateBlocked}
        style={{
          ...buttonStyle(V.accent),
          width: '100%',
          padding: '15px 24px',
          fontSize: 14.5,
          fontWeight: 600,
          justifyContent: 'center',
          borderRadius: 12,
          opacity: (submitting || uploading || saveGateBlocked) ? 0.6 : 1,
          cursor: (submitting || uploading || saveGateBlocked) ? 'not-allowed' : 'pointer',
          boxShadow: (submitting || uploading || saveGateBlocked)
            ? 'none'
            : '0 1px 3px rgba(88,86,214,0.35), 0 8px 24px rgba(88,86,214,0.20)',
          transition: 'all 0.18s',
        }}
      >
        {submitting
          ? 'Saving…'
          : uploading
          ? 'Uploading files…'
          : (editIndex >= 0 ? '✓ Update Benchmark Case' : '✓ Save Benchmark Case')}
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Rules section — team-wide authoring guidance that feeds back into
// skill.md / CLAUDE.md. Each rule is a single freeform `content` blob.
// Mirrors the cases section structurally: a dashboard + an inline form,
// sharing the `view` state axis with cases via the parent BenchmarkPage.
// ─────────────────────────────────────────────────────────────────────────────

function RulesDashboard({ rules, loading, error, onAdd, onEdit, onDelete, onExportMarkdown }) {
  const total = rules.length;

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 24,
        marginBottom: 28,
        borderBottom: `1px solid ${V.border}`,
        gap: 16,
      }}>
        <div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 10px',
            background: V.accentSoft,
            color: V.accent,
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            marginBottom: 10,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}>
            <span>📜</span>
            <span>Rules</span>
          </div>
          <h2 style={{
            fontSize: 28,
            fontWeight: 700,
            margin: 0,
            color: V.text,
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
          }}>
            Authoring Rules
          </h2>
          <div style={{ fontSize: 13, color: V.textDim, marginTop: 6 }}>
            Guidance that informs how we write <code>skill.md</code> / <code>CLAUDE.md</code>. Export and hand-paste.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0, marginTop: 4 }}>
          <button
            type="button"
            onClick={onExportMarkdown}
            disabled={total === 0}
            style={{
              ...buttonStyle(total > 0 ? V.green : V.bgHover),
              color: total > 0 ? '#fff' : V.textFaint,
              cursor: total > 0 ? 'pointer' : 'not-allowed',
              boxShadow: total > 0 ? '0 1px 3px rgba(52,199,89,0.3)' : 'none',
            }}
          >
            ⬇ Export as Markdown
            <span style={{
              marginLeft: 6,
              padding: '2px 8px',
              borderRadius: 10,
              background: total > 0 ? 'rgba(255,255,255,0.25)' : V.border,
              color: total > 0 ? '#fff' : V.textFaint,
              fontSize: 11,
              fontWeight: 700,
            }}>
              {total}
            </span>
          </button>
          <button
            type="button"
            onClick={onAdd}
            style={{
              ...buttonStyle(V.accent),
              boxShadow: '0 1px 3px rgba(88,86,214,0.35)',
            }}
          >
            + Add Rule
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '40px 0', color: V.textDim, fontSize: 14 }}>Loading rules…</div>
      ) : error ? (
        <div style={{ padding: 16, background: '#fff5f5', border: `1px solid ${V.red}`, borderRadius: 8, color: V.red, fontSize: 13 }}>
          {error}
        </div>
      ) : total === 0 ? (
        <div style={{
          padding: '48px 24px',
          textAlign: 'center',
          color: V.textDim,
          fontSize: 14,
          border: `1px dashed ${V.border}`,
          borderRadius: 12,
          background: V.bgSubtle,
        }}>
          No rules yet. Add one to seed your team's skill authoring guidance.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rules.map((r, idx) => (
            <div
              key={r.rule_id || idx}
              style={{
                border: `1px solid ${V.border}`,
                borderRadius: 10,
                padding: '14px 16px',
                background: V.bgCard,
                transition: 'all 0.15s',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 12,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = V.bgSubtle;
                e.currentTarget.style.borderColor = V.borderStrong;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = V.bgCard;
                e.currentTarget.style.borderColor = V.border;
              }}
            >
              <div style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                lineHeight: 1.55,
                color: V.text,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {(r.content || '').trim() || '(empty)'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => onEdit(idx)}
                  style={{
                    background: 'transparent',
                    color: V.accent,
                    border: `1px solid ${V.border}`,
                    borderRadius: 8,
                    padding: '7px 13px',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = V.accent;
                    e.currentTarget.style.color = '#fff';
                    e.currentTarget.style.borderColor = V.accent;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = V.accent;
                    e.currentTarget.style.borderColor = V.border;
                  }}
                >
                  ✏ Edit
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(idx)}
                  style={{
                    background: 'transparent',
                    color: V.red,
                    border: `1px solid ${V.border}`,
                    borderRadius: 8,
                    padding: '7px 13px',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = V.red;
                    e.currentTarget.style.color = '#fff';
                    e.currentTarget.style.borderColor = V.red;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = V.red;
                    e.currentTarget.style.borderColor = V.border;
                  }}
                >
                  🗑 Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RulesForm({ editIndex, content, setContent, submitting, onSubmit, onCancel }) {
  return (
    <form onSubmit={onSubmit}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 20,
        marginBottom: 24,
        borderBottom: `1px solid ${V.border}`,
      }}>
        <div>
          <h2 style={{
            fontSize: 24,
            fontWeight: 700,
            margin: 0,
            color: V.text,
            letterSpacing: '-0.02em',
          }}>
            {editIndex >= 0 ? `Edit Rule #${editIndex + 1}` : 'New Rule'}
          </h2>
          <div style={{ fontSize: 12, color: V.textDim, marginTop: 4 }}>
            Team-wide guidance for skill authoring
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          style={{
            ...buttonStyle(V.bgHover),
            color: V.textDim,
            boxShadow: 'none',
          }}
        >
          Cancel
        </button>
      </div>

      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: V.textDim, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Content
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="e.g. product search must use mcp_product_search, not websearch"
          rows={8}
          required
          style={{ ...inputStyle(), marginTop: 6, minHeight: 160 }}
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        style={{
          marginTop: 28,
          width: '100%',
          padding: '14px 20px',
          border: 'none',
          background: V.accent,
          color: '#fff',
          fontSize: 14,
          fontWeight: 600,
          borderRadius: 12,
          opacity: submitting ? 0.6 : 1,
          cursor: submitting ? 'not-allowed' : 'pointer',
          boxShadow: submitting ? 'none' : '0 1px 3px rgba(88,86,214,0.35), 0 8px 24px rgba(88,86,214,0.20)',
          transition: 'all 0.18s',
        }}
      >
        {submitting ? 'Saving…' : (editIndex >= 0 ? '✓ Update Rule' : '✓ Save Rule')}
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BenchmarkPage — root component (state + data flow)
// ─────────────────────────────────────────────────────────────────────────────

export default function BenchmarkPage() {
  // `section` = which feature tab (cases vs rules); `view` = within-section
  // dashboard/form toggle. Independent axes, per MISSION-1258 shape #1.
  const [section, setSection] = useState('cases');
  const [view, setView] = useState('dashboard');
  const [editIndex, setEditIndex] = useState(-1);
  // Server-assigned stable id for the case being edited. `editIndex` above
  // is kept for the "#N" display label; `editCaseId` is what PUT/DELETE use
  // so concurrent mutations on other cases can't cause the wrong-target bug.
  const [editCaseId, setEditCaseId] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [intention, setIntention] = useState('');
  const [prompt, setPrompt] = useState('');
  const [visualUrls, setVisualUrls] = useState([]);
  const [refUrls, setRefUrls] = useState([]);
  const [refText, setRefText] = useState('');
  const [refUrlInput, setRefUrlInput] = useState('');
  const [refUrlError, setRefUrlError] = useState('');
  const [metrics, setMetrics] = useState(['']);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [tagFilter, setTagFilter] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Scaffold (URL import) state — populated when user pastes a playground
  // share URL and clicks Run. `autoFilledFields` tracks which sections the
  // LLM populated; `touchedAutoFields` tracks which of those the user has
  // focused (UI review guard — Save is blocked until every auto field is
  // focused at least once). `referenceNote` is a read-only tooltip surfaced
  // at the top of the form.
  const [scaffoldUrl, setScaffoldUrl] = useState('');
  const [scaffoldLoading, setScaffoldLoading] = useState(false);
  const [scaffoldError, setScaffoldError] = useState('');
  const [referenceNote, setReferenceNote] = useState('');
  const [autoFilledFields, setAutoFilledFields] = useState(() => new Set());
  const [touchedAutoFields, setTouchedAutoFields] = useState(() => new Set());
  const [scaffoldTurns, setScaffoldTurns] = useState([]);
  const [scaffoldResp, setScaffoldResp] = useState(null);
  const [selectedTurnIndex, setSelectedTurnIndex] = useState(null);

  // --- Rules state (sibling section; loaded lazily on first switch) ---
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState(null);
  const [rulesLoaded, setRulesLoaded] = useState(false);

  const [ruleContent, setRuleContent] = useState('');
  const [editRuleIndex, setEditRuleIndex] = useState(-1);
  const [editRuleId, setEditRuleId] = useState(null);
  const [ruleSubmitting, setRuleSubmitting] = useState(false);

  // Load cases on mount.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.listBenchmarkCases()
      .then((data) => {
        if (cancelled) return;
        setCases(Array.isArray(data?.cases) ? data.cases : []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'Failed to load benchmark cases');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const resetForm = () => {
    setIntention('');
    setPrompt('');
    setVisualUrls([]);
    setRefUrls([]);
    setRefText('');
    setRefUrlInput('');
    setRefUrlError('');
    setMetrics(['']);
    setTags([]);
    setTagInput('');
    setScaffoldUrl('');
    setScaffoldError('');
    setReferenceNote('');
    setAutoFilledFields(new Set());
    setTouchedAutoFields(new Set());
    setScaffoldTurns([]);
    setScaffoldResp(null);
  };

  const openForm = (index = -1) => {
    if (index >= 0 && cases[index]) {
      const c = cases[index];
      setIntention(c?.input?.intention || '');
      setPrompt(c?.input?.messages?.[0]?.content || '');
      setVisualUrls(Array.isArray(c?.input?.messages?.[0]?.image_urls) ? c.input.messages[0].image_urls : []);
      setRefUrls(Array.isArray(c?.expected_output?.references?.files) ? c.expected_output.references.files : []);
      setRefText(typeof c?.expected_output?.references?.text === 'string' ? c.expected_output.references.text : '');
      const cps = Array.isArray(c?.expected_output?.checkpoints) ? c.expected_output.checkpoints : [];
      setMetrics(cps.length ? cps.map((cp) => cp?.description || '') : ['']);
      setTags(Array.isArray(c?.tags) ? c.tags : []);
      setTagInput('');
      setEditIndex(index);
      setEditCaseId(c?.case_id || null);
    } else {
      resetForm();
      setEditIndex(-1);
      setEditCaseId(null);
    }
    setView('form');
    window.scrollTo(0, 0);
  };

  const closeForm = () => {
    setView('dashboard');
    setEditIndex(-1);
    setEditCaseId(null);
    resetForm();
    window.scrollTo(0, 0);
  };

  // Parse a playground share URL (or bare token) into its fernet token.
  // Accepts both: "https://.../playground/share/{token}" and "{token}".
  const parseShareToken = (raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    const m = trimmed.match(/\/playground\/share\/([^/?#]+)/);
    if (m) return m[1];
    if (!trimmed.includes('/') && !trimmed.includes(' ')) return trimmed;
    return '';
  };

  const markAutoFieldTouched = (field) => {
    setTouchedAutoFields((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  };

  const applyScaffoldResponse = (resp, turnOverride) => {
    const auto = new Set();
    const turn = turnOverride || null;
    const turnPhotos = turn ? (turn.photos || []) : [];
    const nextVisual = turnPhotos.length > 0
      ? turnPhotos
      : Array.isArray(resp?.visual_material) ? resp.visual_material : [];
    if (nextVisual.length > 0) {
      setVisualUrls(nextVisual);
      auto.add('visual');
    }
    const nextPrompt = turn
      ? turn.prompt || ''
      : typeof resp?.input_prompt === 'string' ? resp.input_prompt : '';
    if (nextPrompt) {
      setPrompt(nextPrompt);
      auto.add('prompt');
    }
    const nextIntention = typeof resp?.intention === 'string' ? resp.intention : '';
    if (nextIntention) {
      setIntention(nextIntention);
      auto.add('intention');
    }
    const nextCps = Array.isArray(resp?.checkpoints) ? resp.checkpoints.filter((c) => typeof c === 'string' && c.trim()) : [];
    if (nextCps.length > 0) {
      setMetrics(nextCps);
      auto.add('metrics');
    }
    const rawRefs = turn
      ? [...(turn.agent_images || []), ...(Array.isArray(resp?.reference_files) ? resp.reference_files : [])].filter((v, i, a) => a.indexOf(v) === i)
      : Array.isArray(resp?.reference_files) ? resp.reference_files : [];
    const baseRefs = rawRefs.filter((u) => !isProductUrl(u));
    const shareUrl = scaffoldUrl.trim();
    const nextRefs = shareUrl
      ? [shareUrl, ...baseRefs].filter((v, i, a) => a.indexOf(v) === i)
      : baseRefs;
    if (nextRefs.length > 0) {
      setRefUrls(nextRefs);
      auto.add('refs');
    }
    setReferenceNote(typeof resp?.reference_note === 'string' ? resp.reference_note : '');
    setAutoFilledFields(auto);
    setTouchedAutoFields(new Set());
    if (auto.size === 0) {
      setScaffoldError('Scaffold returned no usable fields — author the case manually.');
    }
  };

  const handleHighlightTurn = (turn) => {
    setSelectedTurnIndex((prev) => (prev === turn.index ? null : turn.index));
  };

  const handleConfirmTurn = async () => {
    const turn = scaffoldTurns.find((t) => t.index === selectedTurnIndex);
    if (!turn) return;
    const token = parseShareToken(scaffoldUrl);
    if (!token) {
      applyScaffoldResponse(scaffoldResp, turn);
      setSelectedTurnIndex(null);
      return;
    }
    setScaffoldLoading(true);
    setScaffoldError('');
    try {
      const resp = await api.scaffoldBenchmarkCase(token, turn.index);
      applyScaffoldResponse(resp, turn);
    } catch {
      applyScaffoldResponse(scaffoldResp, turn);
    } finally {
      setScaffoldLoading(false);
      setSelectedTurnIndex(null);
    }
  };

  const handleRemoveTurn = (index) => {
    if (selectedTurnIndex === index) setSelectedTurnIndex(null);
    setScaffoldTurns((prev) => prev.filter((t) => t.index !== index));
  };

  const runScaffold = async () => {
    if (scaffoldLoading) return;
    const token = parseShareToken(scaffoldUrl);
    if (!token) {
      setScaffoldError('Paste a playground share URL or token.');
      return;
    }
    setScaffoldLoading(true);
    setScaffoldError('');
    setScaffoldTurns([]);
    setScaffoldResp(null);
    setSelectedTurnIndex(null);
    try {
      const resp = await api.scaffoldBenchmarkCase(token);
      const turns = Array.isArray(resp?.turns) ? resp.turns : [];
      if (turns.length > 1) {
        setScaffoldTurns(turns);
        setScaffoldResp(resp);
        setReferenceNote(typeof resp?.reference_note === 'string' ? resp.reference_note : '');
      } else {
        applyScaffoldResponse(resp, null);
      }
    } catch (err) {
      const msg = err?.message || String(err);
      if (/invalid|expired|share\s*token/i.test(msg)) {
        setScaffoldError('Share link is invalid or expired.');
      } else {
        setScaffoldError(`Scaffold failed: ${msg}`);
      }
    } finally {
      setScaffoldLoading(false);
    }
  };

  const uploadFiles = async (files, setter) => {
    setUploading(true);
    try {
      for (const file of files) {
        try {
          const ext = getExt(file);
          const url = await api.uploadBlob(file, ext, { useProxyUrl: true });
          setter((prev) => [...prev, url]);
        } catch (err) {
          alert(`Failed to upload ${file.name}: ${err?.message || err}`);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const handleVisualFiles = (files) => uploadFiles(files, setVisualUrls);
  const handleRefFiles = (files) => uploadFiles(files, setRefUrls);

  const removeVisual = (idx) => setVisualUrls((prev) => prev.filter((_, i) => i !== idx));
  const removeRef = (idx) => setRefUrls((prev) => prev.filter((_, i) => i !== idx));

  const addTag = (raw) => {
    const t = (raw || '').trim().toLowerCase();
    if (!t) return;
    setTags((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setTagInput('');
  };
  const removeTag = (i) =>
    setTags((prev) => prev.filter((_, idx) => idx !== i));
  const toggleTagFilter = (tag) =>
    setTagFilter((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );

  const addRefUrl = () => {
    const raw = refUrlInput.trim();
    if (!raw) return;
    let parsed;
    try { parsed = new URL(raw); } catch (_) {
      setRefUrlError('Enter a full URL starting with http:// or https://');
      return;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      setRefUrlError('Only http:// and https:// URLs are allowed');
      return;
    }
    setRefUrls((prev) => (prev.includes(raw) ? prev : [...prev, raw]));
    setRefUrlInput('');
    setRefUrlError('');
  };

  const updateMetric = (i, value) =>
    setMetrics((prev) => prev.map((m, idx) => (idx === i ? value : m)));
  const addMetric = () => setMetrics((prev) => [...prev, '']);
  const removeMetric = (i) =>
    setMetrics((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const filledMetricCount = metrics.filter((m) => m.trim()).length;
  const weightPercent = filledMetricCount > 0 ? Math.round(100 / filledMetricCount) : 0;

  const suggestedTags = useMemo(() => {
    const fromCases = new Set(cases.flatMap((c) => c.tags || []));
    DEFAULT_TAGS.forEach((t) => fromCases.add(t));
    return [...fromCases].sort();
  }, [cases]);

  const submitForm = async (e) => {
    e.preventDefault();
    if (submitting || uploading) return;
    setSubmitting(true);
    try {
      const filteredMetrics = metrics.map((m) => m.trim()).filter(Boolean);
      const weight =
        filteredMetrics.length > 0
          ? Number((1 / filteredMetrics.length).toFixed(2))
          : 0;
      const checkpoints = filteredMetrics.map((desc, i) => ({
        id: `cp_${i + 1}`,
        description: desc,
        weight,
      }));
      const refTextTrimmed = refText.trim();
      const newCase = {
        input: {
          intention: intention.trim(),
          messages: [
            {
              role: 'user',
              content: prompt.trim(),
              image_urls: visualUrls,
            },
          ],
        },
        expected_output: {
          role: 'assistant',
          checkpoints,
          references: refTextTrimmed
            ? { files: refUrls, text: refTextTrimmed }
            : { files: refUrls },
        },
        tags: tags,
      };

      const result =
        editCaseId
          ? await api.updateBenchmarkCase(editCaseId, newCase)
          : await api.createBenchmarkCase(newCase);

      setCases(Array.isArray(result?.cases) ? result.cases : []);
      closeForm();
    } catch (err) {
      alert(`Failed to save: ${err?.message || err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCase = async (idx) => {
    const target = cases[idx];
    const targetCaseId = target?.case_id;
    if (!targetCaseId) {
      alert('Cannot delete: case is missing a stable id. Reload and try again.');
      return;
    }
    if (!window.confirm(`Delete case #${idx + 1}?`)) return;
    try {
      const result = await api.deleteBenchmarkCase(targetCaseId);
      setCases(Array.isArray(result?.cases) ? result.cases : []);
    } catch (err) {
      alert(`Failed to delete: ${err?.message || err}`);
    }
  };

  const exportJson = () => {
    if (cases.length === 0) return;
    const blob = new Blob([JSON.stringify(cases, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'benchmark_cases_export.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // --- Rules section: loader + form helpers ---

  const loadRules = () => {
    setRulesLoading(true);
    setRulesError(null);
    api.listBenchmarkRules()
      .then((data) => {
        setRules(Array.isArray(data?.rules) ? data.rules : []);
        setRulesLoading(false);
        setRulesLoaded(true);
      })
      .catch((e) => {
        setRulesError(e?.message || 'Failed to load rules');
        setRulesLoading(false);
      });
  };

  // Lazy-load rules the first time the user enters the rules section.
  useEffect(() => {
    if (section === 'rules' && !rulesLoaded && !rulesLoading) {
      loadRules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const resetRuleForm = () => {
    setRuleContent('');
  };

  const openRuleForm = (index = -1) => {
    if (index >= 0 && rules[index]) {
      const r = rules[index];
      setRuleContent(r.content || '');
      setEditRuleIndex(index);
      setEditRuleId(r.rule_id || null);
    } else {
      resetRuleForm();
      setEditRuleIndex(-1);
      setEditRuleId(null);
    }
    setView('form');
    window.scrollTo(0, 0);
  };

  const closeRuleForm = () => {
    setView('dashboard');
    setEditRuleIndex(-1);
    setEditRuleId(null);
    resetRuleForm();
    window.scrollTo(0, 0);
  };

  const submitRuleForm = async (e) => {
    e.preventDefault();
    if (ruleSubmitting) return;
    setRuleSubmitting(true);
    try {
      const payload = { content: ruleContent.trim() };
      const result = editRuleId
        ? await api.updateBenchmarkRule(editRuleId, payload)
        : await api.createBenchmarkRule(payload);
      setRules(Array.isArray(result?.rules) ? result.rules : []);
      closeRuleForm();
    } catch (err) {
      alert(`Failed to save rule: ${err?.message || err}`);
    } finally {
      setRuleSubmitting(false);
    }
  };

  const deleteRule = async (index) => {
    const target = rules[index];
    const targetId = target?.rule_id;
    if (!targetId) {
      alert('Cannot delete: rule is missing a stable id. Reload and try again.');
      return;
    }
    if (!window.confirm(`Delete rule #${index + 1}?`)) return;
    try {
      const result = await api.deleteBenchmarkRule(targetId);
      setRules(Array.isArray(result?.rules) ? result.rules : []);
    } catch (err) {
      alert(`Failed to delete rule: ${err?.message || err}`);
    }
  };

  const exportRulesMarkdown = async () => {
    try {
      const markdown = await api.getBenchmarkRulesMarkdown();
      const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'benchmark_rules.md';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Failed to export: ${err?.message || err}`);
    }
  };

  const goToSection = (next) => {
    if (next === section) return;
    setSection(next);
    setView('dashboard');
  };

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        padding: '32px 24px 64px',
        fontFamily: V.fontUi,
        color: V.text,
        background: `radial-gradient(ellipse at top, ${V.accentSoft} 0%, ${V.bg} 60%)`,
      }}
    >
      <div
        style={{
          maxWidth: 920,
          margin: '0 auto',
          background: V.bgCard,
          padding: '32px 36px 36px',
          borderRadius: 20,
          border: `1px solid ${V.border}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 12px 32px rgba(88,86,214,0.06)',
        }}
      >
        <div
          role="tablist"
          aria-label="Benchmark sections"
          style={{
            display: 'inline-flex',
            gap: 4,
            padding: 4,
            background: V.bgHover,
            borderRadius: 10,
            border: `1px solid ${V.border}`,
            marginBottom: 24,
          }}
        >
          {[
            { value: 'cases', label: 'Cases' },
            { value: 'rules', label: 'Rules' },
          ].map((s) => {
            const active = s.value === section;
            return (
              <button
                key={s.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => goToSection(s.value)}
                style={{
                  padding: '7px 18px',
                  borderRadius: 7,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  background: active ? V.bgCard : 'transparent',
                  color: active ? V.text : V.textDim,
                  boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {section === 'cases' ? (
          view === 'dashboard' ? (
            <DashboardView
              cases={cases}
              loading={loading}
              error={error}
              onAdd={() => openForm()}
              onEdit={openForm}
              onDelete={deleteCase}
              onExport={exportJson}
              selectedTags={tagFilter}
              onToggleTagFilter={toggleTagFilter}
            />
          ) : (
            <FormView
              editIndex={editIndex}
              intention={intention}
              setIntention={setIntention}
              prompt={prompt}
              setPrompt={setPrompt}
              visualUrls={visualUrls}
              onVisualFiles={handleVisualFiles}
              onRemoveVisual={removeVisual}
              refUrls={refUrls}
              onRefFiles={handleRefFiles}
              onRemoveRef={removeRef}
              refUrlInput={refUrlInput}
              onRefUrlInputChange={setRefUrlInput}
              onAddRefUrl={addRefUrl}
              refUrlError={refUrlError}
              refText={refText}
              onRefTextChange={setRefText}
              metrics={metrics}
              onUpdateMetric={updateMetric}
              onAddMetric={addMetric}
              onRemoveMetric={removeMetric}
              weightPercent={weightPercent}
              tags={tags}
              tagInput={tagInput}
              onTagInputChange={setTagInput}
              onAddTag={addTag}
              onRemoveTag={removeTag}
              suggestedTags={suggestedTags}
              uploading={uploading}
              submitting={submitting}
              onSubmit={submitForm}
              onCancel={closeForm}
              scaffoldUrl={scaffoldUrl}
              onScaffoldUrlChange={setScaffoldUrl}
              scaffoldLoading={scaffoldLoading}
              scaffoldError={scaffoldError}
              onRunScaffold={runScaffold}
              referenceNote={referenceNote}
              autoFilledFields={autoFilledFields}
              onTouchAutoField={markAutoFieldTouched}
              scaffoldTurns={scaffoldTurns}
              selectedTurnIndex={selectedTurnIndex}
              onHighlightTurn={handleHighlightTurn}
              onConfirmTurn={handleConfirmTurn}
              onRemoveTurn={handleRemoveTurn}
              saveGateBlocked={false}
            />
          )
        ) : view === 'dashboard' ? (
          <RulesDashboard
            rules={rules}
            loading={rulesLoading}
            error={rulesError}
            onAdd={() => openRuleForm()}
            onEdit={openRuleForm}
            onDelete={deleteRule}
            onExportMarkdown={exportRulesMarkdown}
          />
        ) : (
          <RulesForm
            editIndex={editRuleIndex}
            content={ruleContent}
            setContent={setRuleContent}
            submitting={ruleSubmitting}
            onSubmit={submitRuleForm}
            onCancel={closeRuleForm}
          />
        )}
      </div>
    </div>
  );
}
